import { ProgressCircle } from '@react-spectrum/s2';
import Calendar from '@react-spectrum/s2/icons/Calendar';
import User from '@react-spectrum/s2/icons/User';
import { iconStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import type { ReactNode } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import {
	EMAIL_GENERATING_TITLE,
	EMAIL_STEP_TITLES,
	getEmailPanelTitle,
} from '@/components/artifact/panelMeta';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';

import type {
	Action as ProcessAction,
	RequestOwnerActionConfig,
} from '../../../../../../bridge/types/action.ts';
import type { Artifact } from '../../../../../../bridge/types/artifact.ts';
import { EMAIL_STEPS } from '../../../../../../bridge/types/setup-steps.ts';
import type { ProcessSetupSnapshot } from '../../../../../../bridge/types/state.ts';
import {
	isSkipped,
	outstandingSetupItems,
} from '../../../../../../bridge/utils/setupCardItems.ts';
import { getPanelIcon } from '../panelTypeIcons.tsx';

import { ownerActionRowTitle } from './ownerActionRowTitle.ts';
import { SetupCardRow } from './SetupCardRow.tsx';
import { artifactRowTitle, setupItemLabel } from './setupRowTitle.ts';

const recipientsIconStyles = iconStyle({ size: 'L' });
const recipientsIconMutedStyles = iconStyle({ size: 'L', color: 'gray' });

type SetupStatus = 'pending' | 'generating' | 'complete' | 'error';

const StatusBadge = ({ status }: { status: SetupStatus }) => {
	if (status === 'error') {
		return (
			<div
				className="flex size-4 items-center justify-center rounded-full
					bg-red-700 text-white ring-2 ring-white"
				aria-label="Generation failed"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 20 20"
					fill="currentColor"
					className="size-3"
					aria-hidden="true"
				>
					<path
						fillRule="evenodd"
						d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
						clipRule="evenodd"
					/>
				</svg>
			</div>
		);
	}
	if (status === 'complete') {
		return (
			<div
				className="flex size-4 items-center justify-center rounded-full
					bg-gray-900 text-white ring-2 ring-white"
				aria-label="Complete"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 20 20"
					fill="currentColor"
					className="size-3"
					aria-hidden="true"
				>
					<path
						fillRule="evenodd"
						d="M16.704 5.29a.75.75 0 010 1.06l-8.25 8.25a.75.75 0 01-1.06 0l-3.75-3.75a.75.75 0 011.06-1.06L7.875 12.94l7.77-7.77a.75.75 0 011.06.06z"
						clipRule="evenodd"
					/>
				</svg>
			</div>
		);
	}
	if (status === 'generating') {
		return (
			<div className="rounded-full bg-white ring-2 ring-white">
				<ProgressCircle size="S" isIndeterminate aria-label="Generating" />
			</div>
		);
	}
	return null;
};

const IconWithStatus = ({
	icon,
	status,
}: {
	icon: ReactNode;
	status: SetupStatus;
}) => (
	<div className="relative flex size-full items-center justify-center">
		{icon}
		{status !== 'pending' && (
			<div className="absolute -right-0.5 -bottom-0.5">
				<StatusBadge status={status} />
			</div>
		)}
	</div>
);

const isArtifactGenerating = (
	artifact: Artifact,
	outstandingRequests: Record<
		string,
		{ type: string; artifact_id?: string | null } | null | undefined
	>,
): boolean => {
	if (artifact.type === 'email_html') {
		const content = artifact.content;
		if (
			!!content.selected_template_id &&
			content.instance == null &&
			!content.last_generation_error
		) {
			return true;
		}

		return Object.values(outstandingRequests).some(
			(req) =>
				req?.type === 'do_generate_email_for_template' &&
				req.artifact_id === artifact.id,
		);
	}

	if (artifact.type === 'instagram_post' || artifact.type === 'digital_ad') {
		return Object.values(outstandingRequests).some(
			(req) =>
				req?.type === 'do_generate_post_images_for_artifact' &&
				req.artifact_id === artifact.id,
		);
	}

	return false;
};

export type SetupCardProps =
	| {
			kind: 'artifact';
			snapshot: ProcessSetupSnapshot;
			artifact: Artifact;
			fallbackTitle: string;
			emailIndex?: number;
			emailTotal?: number;
			postIndex?: number;
			postTotal?: number;
			isInert?: boolean;
			isLocked?: boolean;
			showSkip?: boolean;
			canSkip?: boolean;
			skipDisabledReason?: string;
	  }
	| {
			kind: 'action';
			snapshot: ProcessSetupSnapshot;
			action: ProcessAction;
			isInert?: boolean;
			isLocked?: boolean;
			showSkip?: boolean;
			canSkip?: boolean;
	  }
	| {
			kind: 'recipients';
			processId: string;
			isFinalized: boolean;
			isInert?: boolean;
			isLocked?: boolean;
	  }
	| {
			kind: 'schedule';
			snapshot: ProcessSetupSnapshot;
			// 'ownerOnly': the campaign is the owner's own steps only. Nothing is
			// sent or published, so this row reviews the plan and starts it.
			// 'monitorOnly': the campaign is monitors only. Each monitor already
			// holds its own cadence, or it runs from the Instagram webhook, so this
			// row has no date to ask for — it just turns them on.
			// A single derived value rather than two independent booleans, so every
			// consumer resolves the (mutually-exclusive-by-construction) modes the
			// same way instead of re-deriving its own ownerOnly-wins-if-both order.
			reviewMode?: 'ownerOnly' | 'monitorOnly' | 'alwaysOnOnly' | 'normal';
	  };

export const SetupCard = (props: SetupCardProps) => {
	const bridgeApi = useBridgeApi();
	const goToNextStep = useGoToNextStep();
	const openChatPanel = useOpenChatPanel();

	const chatPanel = bridgeApi.user_state?.chat_panel;

	if (props.kind === 'schedule') {
		const { snapshot } = props;
		const outstanding = outstandingSetupItems(snapshot);
		const isInert = outstanding.length > 0;
		const labels = outstanding.map((item) => setupItemLabel(snapshot, item));
		const isSelected =
			chatPanel?.panel_type === 'process_schedule' &&
			chatPanel.item_id === snapshot.process_id;
		const handleOpen = (): void => {
			openChatPanel({
				panel_type: 'process_schedule',
				item_id: snapshot.process_id,
			});
		};
		// "Complete steps above to schedule" left the owner counting rows to work
		// out what was still missing. Name the step when there's only one left,
		// and count them otherwise; the full list rides along in inertReason.
		const scheduleCopy: Record<
			'ownerOnly' | 'monitorOnly' | 'alwaysOnOnly' | 'normal',
			{ description: string; title: string }
		> = {
			ownerOnly: {
				description: 'Check the plan, then start the campaign',
				title: 'Review and start',
			},
			monitorOnly: {
				description: 'Turn your monitors on',
				title: 'Start monitoring',
			},
			alwaysOnOnly: {
				description: 'Check the ads, then start the campaign',
				title: 'Review and start',
			},
			normal: {
				description: 'Choose when each item sends',
				title: 'Schedule and Activate',
			},
		};
		const reviewMode = props.reviewMode ?? 'normal';
		const description = !isInert
			? scheduleCopy[reviewMode].description
			: labels.length === 1
				? `1 step left: ${labels[0]}`
				: `${labels.length} steps left`;
		return (
			<SetupCardRow
				icon={
					<IconWithStatus
						icon={
							<Calendar
								styles={
									isInert ? recipientsIconMutedStyles : recipientsIconStyles
								}
							/>
						}
						status="pending"
					/>
				}
				title={scheduleCopy[reviewMode].title}
				description={description}
				onClick={handleOpen}
				isInert={isInert}
				inertReason={isInert ? `Waiting on: ${labels.join(', ')}` : undefined}
				isSelected={isSelected}
				descriptionPlacement="right-helper"
			/>
		);
	}

	// An owner ask has no artifact — the instruction lives on the action itself —
	// so it needs its own row. Without one the item is invisible while still
	// gating go-live, and the campaign can never be started.
	if (props.kind === 'action') {
		const { action, snapshot } = props;
		const config = action.config as RequestOwnerActionConfig;
		const isCompleted = (snapshot.completed_item_ids ?? []).includes(action.id);
		const isSkippedAction = (snapshot.skipped_action_ids ?? []).includes(
			action.id,
		);
		const isInert = props.isInert ?? false;
		const status: SetupStatus = isCompleted ? 'complete' : 'pending';
		const isSelected =
			chatPanel?.panel_type === 'action' && chatPanel.item_id === action.id;
		const handleOpen = (): void => {
			openChatPanel({
				panel_type: 'action',
				item_id: action.id,
				artifact_type: action.type,
			});
		};
		const handleSkipAction = (): void => {
			bridgeApi.sendAction({
				type: 'do_skip_action',
				process_id: snapshot.process_id,
				action_id: action.id,
			});
		};
		const handleRestoreAction = (): void => {
			bridgeApi.sendAction({
				type: 'do_unskip_action',
				process_id: snapshot.process_id,
				action_id: action.id,
			});
		};
		return (
			<SetupCardRow
				icon={
					<IconWithStatus
						icon={getPanelIcon('request_owner_action')}
						status={isSkippedAction ? 'pending' : status}
					/>
				}
				title={ownerActionRowTitle(config.instruction, 'Your part')}
				description={isSkippedAction ? 'Skipped' : 'Something only you can do'}
				onClick={handleOpen}
				isInert={isInert}
				isCompleted={isCompleted}
				isSelected={isSelected}
				isSkipped={isSkippedAction}
				showSkip={(props.showSkip ?? false) && !isCompleted}
				canSkip={props.canSkip ?? true}
				onSkip={handleSkipAction}
				onRestore={handleRestoreAction}
				descriptionPlacement={
					isSkippedAction || isCompleted ? 'right-status' : 'right-helper'
				}
			/>
		);
	}

	if (props.kind === 'recipients') {
		const isCompleted = props.isFinalized;
		const isInert = props.isInert ?? false;
		const isLocked = props.isLocked ?? false;
		const status: SetupStatus = isCompleted ? 'complete' : 'pending';
		const isSelected =
			chatPanel?.panel_type === 'process_recipients' &&
			chatPanel.item_id === props.processId;
		const handleOpen = (): void => {
			openChatPanel({
				panel_type: 'process_recipients',
				item_id: props.processId,
			});
		};
		const muted = isInert || isCompleted;
		return (
			<SetupCardRow
				icon={
					<IconWithStatus
						icon={
							<User
								styles={
									muted ? recipientsIconMutedStyles : recipientsIconStyles
								}
							/>
						}
						status={status}
					/>
				}
				title="Choose Recipients"
				description={
					isCompleted ? 'Completed' : 'Choose who receives this campaign'
				}
				onClick={handleOpen}
				isInert={isInert}
				isCompleted={isCompleted}
				isLocked={isLocked}
				isSelected={isSelected}
				descriptionPlacement={isCompleted ? 'right-status' : 'right-helper'}
			/>
		);
	}

	const {
		snapshot,
		artifact,
		fallbackTitle,
		emailIndex,
		emailTotal,
		postIndex,
		postTotal,
		showSkip,
		canSkip,
		skipDisabledReason,
	} = props;
	const completedIds = new Set(snapshot.completed_item_ids ?? []);
	const isCompleted = completedIds.has(artifact.id);
	const isInert = props.isInert ?? false;
	const isLocked = props.isLocked ?? false;
	const isSkippedArtifact = isSkipped(snapshot, artifact.id);
	const handleOpenEditor = (): void => {
		openChatPanel({
			panel_type: 'artifact',
			item_id: artifact.id,
			artifact_type: artifact.type,
		});
	};
	const handleSkip = (): void => {
		bridgeApi.sendAction({
			type: 'do_skip_artifact',
			process_id: snapshot.process_id,
			artifact_id: artifact.id,
		});
		// If the right panel is showing the item being skipped, advance it to the
		// next outstanding step (mirrors the post-approval auto-advance). The
		// just-skipped id is passed so the next-step finder steps over it.
		const panelOpenOnThisArtifact =
			chatPanel?.panel_type === 'artifact' && chatPanel.item_id === artifact.id;
		if (panelOpenOnThisArtifact) {
			goToNextStep(snapshot.process_id, artifact.id);
		}
	};
	const handleRestore = (): void => {
		bridgeApi.sendAction({
			type: 'do_unskip_artifact',
			process_id: snapshot.process_id,
			artifact_id: artifact.id,
		});
	};

	const outstandingRequests = bridgeApi.user_state?.outstanding_requests ?? {};
	const isGenerating =
		!isCompleted && isArtifactGenerating(artifact, outstandingRequests);
	// Error-truthy is the whole signal: a failed retry can retain stale
	// image_urls, so gating on "no images" would hide it (ADO-1469). A live
	// generation always wins — isGenerating is checked first.
	const isError =
		!isCompleted &&
		!isGenerating &&
		!!(artifact.content as { last_generation_error?: unknown })
			.last_generation_error;
	const isEmail =
		artifact.type === 'email_html' || artifact.type === 'email_plaintext';
	const isPost = artifact.type === 'instagram_post';
	const title = artifactRowTitle(artifact, {
		emailIndex,
		emailTotal,
		postIndex,
		postTotal,
		fallbackTitle,
	});

	const stepIndex =
		bridgeApi.user_state?.panel_step_by_artifact_id?.[artifact.id] ?? 0;
	const emailStepKey =
		isEmail && artifact.type === 'email_html'
			? EMAIL_STEPS[Math.min(stepIndex, EMAIL_STEPS.length - 1)]
			: undefined;

	const description = isCompleted
		? 'Completed'
		: isError
			? 'Generation failed — Retry'
			: isEmail
				? isGenerating
					? EMAIL_GENERATING_TITLE
					: emailStepKey
						? getEmailPanelTitle(emailStepKey, false)
						: EMAIL_STEP_TITLES.EmailTemplateSelection
				: isPost
					? isGenerating
						? 'Generating…'
						: 'Review Content'
					: isGenerating
						? 'Generating…'
						: artifact.type === 'digital_ad'
							? 'Click to review'
							: 'Click to review & approve';

	const status: SetupStatus = isCompleted
		? 'complete'
		: isGenerating
			? 'generating'
			: isError
				? 'error'
				: 'pending';
	const typeIcon = getPanelIcon(artifact.type, {
		muted: isInert || isCompleted || isSkippedArtifact,
	});

	const isSelected =
		chatPanel?.panel_type === 'artifact' && chatPanel.item_id === artifact.id;

	// Generating or already-approved items can't be skipped; an item already in
	// progress on Express is locked, so the menu hides for those too.
	const skipAvailable = !isCompleted && !isLocked && !isGenerating;
	// A skipped row reads as a plain muted "Skipped" — its generation/error
	// states are irrelevant because it no longer activates.
	const rowDescription = isSkippedArtifact ? 'Skipped' : description;
	const rowPlacement: 'right-status' | 'right-helper' = isSkippedArtifact
		? 'right-status'
		: isCompleted || isGenerating || isError
			? 'right-status'
			: 'right-helper';
	const rowStatus: SetupStatus = isSkippedArtifact ? 'pending' : status;

	return (
		<SetupCardRow
			icon={<IconWithStatus icon={typeIcon} status={rowStatus} />}
			title={title}
			description={rowDescription}
			onClick={handleOpenEditor}
			isInert={isInert}
			isCompleted={isCompleted}
			isInProgress={isSkippedArtifact ? false : isGenerating}
			isError={isSkippedArtifact ? false : isError}
			isLocked={isLocked}
			isSelected={isSelected}
			isSkipped={isSkippedArtifact}
			showSkip={(showSkip ?? false) && skipAvailable}
			canSkip={canSkip ?? true}
			skipDisabledReason={skipDisabledReason}
			onSkip={handleSkip}
			onRestore={handleRestore}
			descriptionPlacement={rowPlacement}
		/>
	);
};
